import React, { useEffect } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { ChevronRight, Filter, Sparkles, SlidersHorizontal, ArrowLeft } from 'lucide-react';
import ProductCard from '../components/ProductCard';

const PREDEFINED_CATEGORY_META = {
  all: {
    name: 'All Jewellery Collections',
    subtitle: '100% BIS Hallmarked Pure Gold, Solitaires & Regal Kitchenware',
    badge: 'Complete Imperial Vault'
  },
  bridal: {
    name: 'Royal Bridal Jewellery',
    subtitle: 'Imperial Wedding Kundan, Polki & Heritage Choker Sets',
    badge: 'Rajputana Heritage'
  },
  rings: {
    name: 'Diamond & Solitaire Rings',
    subtitle: 'Handcrafted Navratna, Solitaire Diamonds & Royal Cocktail Bands',
    badge: 'Certified Solitaires'
  },
  necklaces: {
    name: 'Necklaces & Chokers',
    subtitle: 'Padmavati Kundan Sets, Rani Haars & Royal Chokers',
    badge: 'Artisan Crafted'
  },
  earrings: {
    name: 'Chandbalis & Jhumkas',
    subtitle: 'Natural Pearl Latkans, Regal Chandbalis & Solitaire Studs',
    badge: 'Fine Polki Work'
  },
  bangles: {
    name: 'Kadas & Gold Bangles',
    subtitle: 'Antique Mayura Temple Kadas & Traditional Gold Bangles',
    badge: 'BIS 916 Hallmarked'
  },
  kitchenware: {
    name: 'Royal Heritage Kitchenware',
    subtitle: 'Regal Tri-Ply Steel & Pure Copper Kalai Handis',
    badge: 'Imperial Dining'
  }
};

export default function CategoryPage() {
  const { categoryId: paramCat } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { 
    products, 
    categories, 
    selectedMetal, 
    setSelectedMetal, 
    sortBy, 
    setSortBy,
    searchQuery,
    setSearchQuery,
    storeSettings
  } = useShop();

  // Resolve category slug from URL parameter or pathname (e.g. /category/rings or /rings)
  const pathSlug = location.pathname.replace(/^\//, '').split('/')[0];
  const directSlugs = ['bridal', 'rings', 'necklaces', 'earrings', 'bangles', 'kitchenware', 'collections', 'all-jewels'];
  
  const categoryId = paramCat || (directSlugs.includes(pathSlug) ? (pathSlug === 'collections' || pathSlug === 'all-jewels' ? 'all' : pathSlug) : 'all');

  const dbCat = (categories || []).find(c => c.id === categoryId);
  const predefinedMeta = PREDEFINED_CATEGORY_META[categoryId];

  const currentCategory = {
    id: categoryId,
    name: dbCat?.name || predefinedMeta?.name || (categoryId === 'all' ? 'All Collections' : categoryId.charAt(0).toUpperCase() + categoryId.slice(1).replace(/-/g, ' ')),
    subtitle: dbCat?.subtitle || predefinedMeta?.subtitle || '100% BIS Hallmarked • Transparent Bullion Pricing',
    badge: dbCat?.badge || predefinedMeta?.badge || 'Certified Collection'
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = `${currentCategory.name} | ${storeSettings?.storeName || 'M.L. Jewellers & Kitchenware'}`;
  }, [categoryId, currentCategory.name]);

  // Master categories list for horizontal navigation switcher
  const navCategories = [
    { id: 'all', name: 'All Jewellery' },
    { id: 'bridal', name: 'Bridal Sets' },
    { id: 'rings', name: 'Diamond Rings' },
    { id: 'necklaces', name: 'Necklaces' },
    { id: 'earrings', name: 'Chandbalis' },
    { id: 'bangles', name: 'Kadas & Bangles' },
    { id: 'kitchenware', name: 'Kitchenware' },
    ...(categories || []).filter(c => c && c.id && !['all', 'bridal', 'rings', 'necklaces', 'earrings', 'bangles', 'kitchenware'].includes(c.id))
  ];

  // Filtering products
  const categoryProducts = (Array.isArray(products) ? products : []).filter(p => {
    if (!p) return false;
    const pCat = String(p.category || '').toLowerCase().trim();
    const targetCat = String(categoryId || 'all').toLowerCase().trim();
    const matchesCat = !categoryId || targetCat === 'all' || pCat === targetCat || pCat.includes(targetCat);

    const q = (searchQuery || '').toLowerCase().trim();
    const matchesSearch = !q || 
      (p.name || '').toLowerCase().includes(q) ||
      (p.subtitle || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q) ||
      (p.metal || '').toLowerCase().includes(q) ||
      (p.gemstone || '').toLowerCase().includes(q);

    const matchesMetal = selectedMetal === 'all' || (p.metal || '').toLowerCase().includes(selectedMetal.toLowerCase());
    return matchesCat && matchesSearch && matchesMetal;
  }).sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    if (sortBy === 'rating') return b.rating - a.rating;
    return (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0);
  });

  return (
    <div style={{ backgroundColor: 'var(--bg-primary, #07070b)', color: '#fff', minHeight: '85vh', paddingBottom: '5rem' }}>
      
      {/* 1. Breadcrumb Bar */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.02)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        padding: '0.85rem 0'
      }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Home</Link>
          <ChevronRight size={14} />
          <Link to="/category/all" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Collections</Link>
          <ChevronRight size={14} />
          <span style={{ color: 'var(--gold-400)', fontWeight: 700 }}>{currentCategory.name}</span>
        </div>
      </div>

      {/* 2. Regal Category Banner with Imperial Radial Glow */}
      <div style={{
        background: 'radial-gradient(ellipse at 50% 30%, #1c1a2e 0%, #0c0b12 75%)',
        padding: '2.5rem 0 2rem',
        borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
        textAlign: 'center',
        position: 'relative'
      }}>
        <div className="container">
          <span style={{
            background: 'rgba(212, 175, 55, 0.15)',
            border: '1px solid var(--border-gold)',
            color: 'var(--gold-300)',
            fontSize: '0.72rem',
            fontWeight: 800,
            padding: '0.25rem 0.75rem',
            borderRadius: '999px',
            textTransform: 'uppercase',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            marginBottom: '0.6rem'
          }}>
            <Sparkles size={12} color="var(--gold-400)" /> {currentCategory.badge}
          </span>
          <h1 style={{
            fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
            fontWeight: 700,
            fontFamily: 'var(--font-serif, "Cinzel", Georgia, serif)',
            margin: '0 0 0.5rem 0',
            letterSpacing: '0.02em',
            color: '#ffffff'
          }}>
            {currentCategory.name}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', maxWidth: '640px', margin: '0 auto' }}>
            {currentCategory.subtitle}
          </p>

          {/* Category Horizontal Quick Switcher Ribbon */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            marginTop: '1.6rem'
          }}>
            {navCategories.map((c) => {
              const isSelected = c.id === categoryId;
              return (
                <button
                  key={c.id}
                  onClick={() => navigate(`/category/${c.id}`)}
                  style={{
                    backgroundColor: isSelected ? 'var(--gold-400)' : 'rgba(255, 255, 255, 0.06)',
                    color: isSelected ? '#0d2346' : '#ffffff',
                    border: isSelected ? '1px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.15)',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: isSelected ? 800 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. Main Catalog View */}
      <div className="container" style={{ paddingTop: '2rem' }}>
        
        {/* Active Search Filter Banner */}
        {searchQuery && searchQuery.trim() && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.75rem',
            padding: '0.85rem 1.25rem',
            backgroundColor: 'rgba(212, 175, 55, 0.1)',
            border: '1px solid var(--border-gold)',
            borderRadius: '10px',
            marginBottom: '1.5rem'
          }}>
            <div style={{ fontSize: '0.9rem', color: '#fff' }}>
              Showing search results for: <strong style={{ color: 'var(--gold-400)' }}>"{searchQuery}"</strong> ({categoryProducts.length} jewellery items found)
            </div>
            <button
              onClick={() => setSearchQuery('')}
              style={{
                backgroundColor: 'rgba(248, 113, 113, 0.15)',
                border: '1px solid #f87171',
                color: '#f87171',
                borderRadius: '6px',
                padding: '0.3rem 0.75rem',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Clear Search ✕
            </button>
          </div>
        )}

        {/* Controls Toolbar (Count, Metal Filter, Sorting) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1.5rem',
          padding: '0.85rem 1.2rem',
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 'var(--radius-sm, 4px)'
        }}>
          <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
            Showing <strong style={{ color: '#fff' }}>{categoryProducts.length}</strong> creations in <strong style={{ color: 'var(--gold-400)' }}>{currentCategory.name}</strong>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {/* Metal Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Metal Purity:</span>
              <select
                value={selectedMetal}
                onChange={(e) => setSelectedMetal(e.target.value)}
                style={{
                  background: '#161622',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  borderRadius: '4px',
                  padding: '0.4rem 0.65rem',
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Metals</option>
                <option value="22k">22KT Gold (916)</option>
                <option value="18k">18KT Gold (750)</option>
                <option value="14k">14KT Gold (585)</option>
                <option value="diamond">Natural Diamonds</option>
              </select>
            </div>

            {/* Sort Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Sort By:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  background: '#161622',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  borderRadius: '4px',
                  padding: '0.4rem 0.65rem',
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                <option value="featured">Featured First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Top Rated (5★)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        {categoryProducts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 1rem',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '8px',
            border: '1px dashed rgba(255, 255, 255, 0.15)'
          }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', marginBottom: '1.5rem' }}>
              No jewellery pieces currently matching your selection in {currentCategory.name}.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  setSelectedMetal('all');
                  setSearchQuery('');
                }}
                className="btn btn-outline"
                style={{ padding: '0.6rem 1.4rem' }}
              >
                Reset Filters
              </button>
              <button
                onClick={() => navigate('/category/all')}
                className="btn btn-gold"
                style={{ padding: '0.6rem 1.4rem' }}
              >
                View All Collections
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '1.5rem'
          }}>
            {categoryProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
